import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef, Input, OnChanges, OnDestroy, OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SpriteCacheService, SpriteInfo } from '../../services/sprite-cache.service';

const ASSET_HOST = 'https://pixelstarships.s3.amazonaws.com';

/**
 * Renders a single PSS sprite. The sprite is a cropped region of a larger
 * spritesheet hosted on S3; we resolve `spriteId → { imageFileId, x, y, w, h }`
 * via SpriteCacheService, then render the cropped region scaled to fit this
 * element's CSS-applied size (so the consumer can use any external sizing
 * rule like `.crew-sprite { width: 64px; height: 64px; }`).
 *
 * If the host has no CSS-applied size, the sprite renders at native size.
 */
@Component({
  selector: 'pss-sprite',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <img *ngIf="info && sheetUrl"
         class="pss-sprite-sheet"
         [src]="sheetUrl"
         [alt]="alt"
         [style.transform]="transform"
         draggable="false"/>
  `,
  styles: [`
    :host {
      display: inline-block;
      overflow: hidden;
      position: relative;
      line-height: 0;
      vertical-align: middle;
    }
    .pss-sprite-sheet {
      position: absolute;
      left: 0;
      top: 0;
      max-width: none;       /* defeat any global img max-width:100% */
      max-height: none;
      image-rendering: pixelated;
      image-rendering: -moz-crisp-edges;
      transform-origin: top left;
      user-select: none;
      pointer-events: none;
    }
  `],
})
export class PssSpriteComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() spriteId: string | number | null | undefined = '';
  @Input() alt: string = '';

  info: SpriteInfo | undefined = undefined;
  sheetUrl: string | null = null;
  transform = '';

  private hostW = 0;
  private hostH = 0;
  private resizeObserver?: ResizeObserver;
  private sub?: Subscription;

  constructor(
    private cache: SpriteCacheService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.cache.ensureLoaded();
    this.sub = this.cache.ready$.subscribe(() => this.refresh());
  }

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        const r = entries[0]?.contentRect;
        if (!r) return;
        if (r.width !== this.hostW || r.height !== this.hostH) {
          this.hostW = r.width;
          this.hostH = r.height;
          this.refresh();
        }
      });
      this.resizeObserver.observe(el);
    } else {
      const rect = el.getBoundingClientRect();
      this.hostW = rect.width;
      this.hostH = rect.height;
    }
    this.refresh();
  }

  ngOnChanges(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.sub?.unsubscribe();
  }

  private refresh(): void {
    const info = this.cache.get(this.spriteId);
    if (!info) {
      this.info = undefined;
      this.sheetUrl = null;
      this.cdr.markForCheck();
      return;
    }
    this.info = info;
    this.sheetUrl = `${ASSET_HOST}/${info.imageFileId}.png`;

    // Pick a scale that fits whichever host dimension is the larger constraint
    // (so a host like 32x26 with a sprite at 30x25 picks ~1.07x, not stretching
    // the aspect ratio). If the host has no measured size yet, render at 1:1.
    let scale = 1;
    if (this.hostW > 0 && this.hostH > 0 && info.width > 0 && info.height > 0) {
      const sx = this.hostW / info.width;
      const sy = this.hostH / info.height;
      scale = Math.min(sx, sy);
    }
    const tx = -info.x * scale;
    const ty = -info.y * scale;
    this.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    this.cdr.markForCheck();
  }
}
